import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
// Utilities
import StoryboardRenderingUtil from 'Utils/content/storyboard-rendering-util';
import { SPACING } from 'Style/spacing';
import { UI_TEXT_TYPES, CONTENT_STORY_HEADING_TYPES } from 'Style/typography';
import { overflowWordBreak } from 'Style/definitions';
import { COLORS, DefaultTextColorStyle } from 'Style/colors';
import { truncate } from 'Style/truncate';
// Components
import Title from '../../components/base/title';
import SourceAttribution from '../../components/attribution/source';
import ItemLink from '../../components/item/item-link';
import ItemExcerpt from '../../components/item/excerpt';

import connector from 'Utils/connector';
import connectResponsive from 'Webapp/shared/app/connectors/connectResponsive';
import connectUsageSetNavFrom from 'Webapp/shared/app/connectors/connectUsageSetNavFrom';

const { sizes } = StoryboardRenderingUtil;

const StyledStoryboardPostTitle = styled.div(
  {
    '.post__title ': {
      display: 'initial',
    },
    '&:not(:last-child)': {
      marginBottom: SPACING.BASE,
    },
  },
  truncate('100%', 3),
);

const StyledStoryboardPostSubtitle = styled.div({
  ...UI_TEXT_TYPES.SUPPORTING,
  '&:not(:last-child)': {
    marginBottom: SPACING.MEDIUM,
  },
});

const StyledStoryboardPostText = styled.div({
  flexGrow: '1',
  ...overflowWordBreak,
});

export const StoryboardPostExcerpt = styled.p();
const StyledStoryboardPrefix = styled('span', {
  shouldForwardProp: (prop) => prop !== 'size',
})(
  (props) => {
    if (props.size === sizes.LARGE) {
      return CONTENT_STORY_HEADING_TYPES.LARGE;
    }
    return CONTENT_STORY_HEADING_TYPES.SMALL;
  },
  {
    color: COLORS.red,
    margin: '2px 5px 0px 0px',
  },
);
const StyledTitleLink = styled(ItemLink, {
  shouldForwardProp: (prop) => prop !== 'isInline',
})(({ isInline }) => DefaultTextColorStyle(!isInline, isInline));

const StyledTitle = styled(Title, {
  shouldForwardProp: (prop) => prop !== 'isInline',
})(
  ({ size }) => {
    if (size === sizes.LARGE) {
      return CONTENT_STORY_HEADING_TYPES.LARGE;
    }
    return CONTENT_STORY_HEADING_TYPES.SMALL;
  },
  { color: 'unset' },
);

function StoryboardPostText({
  item,
  section,
  group,
  index,
  usageSetNavFrom,
  isInline,
}) {
  const renderPostTitle = () => {
    if (item.isStatus) {
      return null;
    }

    const size = StoryboardRenderingUtil.groupSize(group);
    const titleElement = (
      <StyledStoryboardPostTitle className="storyboard-post__title">
        {index !== null && StoryboardRenderingUtil.isNumberedGroup(group) && (
          <StyledStoryboardPrefix size={size}>
            {index + 1}.
          </StyledStoryboardPrefix>
        )}
        <StyledTitleLink
          isInline={isInline}
          item={item}
          section={section}
          shownInList
        >
          <StyledTitle
            primary={false}
            size={size}
            className="post__title"
            item={item}
          />
        </StyledTitleLink>
      </StyledStoryboardPostTitle>
    );

    return titleElement;
  };

  const renderPostExcerpt = () => {
    const size = StoryboardRenderingUtil.groupSize(group);
    if (size !== sizes.LARGE) {
      return null;
    }

    const excerptElement = <ItemExcerpt item={item} />;

    return (
      excerptElement && (
        <StoryboardPostExcerpt className="post-excerpt--text">
          {excerptElement}
        </StoryboardPostExcerpt>
      )
    );
  };

  return (
    <StyledStoryboardPostText className="storyboard-post__text">
      <header className="storyboard-post__header">
        {renderPostTitle()}
        <StyledStoryboardPostSubtitle>
          <SourceAttribution
            item={item}
            section={section}
            onClick={() => usageSetNavFrom('storyboard-layout')}
            shouldRenderAvatar={item.isStatus}
          />
        </StyledStoryboardPostSubtitle>
      </header>
      {renderPostExcerpt()}
    </StyledStoryboardPostText>
  );
}

StoryboardPostText.propTypes = {
  item: PropTypes.object.isRequired,
  section: PropTypes.object.isRequired,
  group: PropTypes.object,
  usageSetNavFrom: PropTypes.func.isRequired,
  index: PropTypes.number,
  isInline: PropTypes.bool,
};

StoryboardPostText.defaultProps = {
  index: null,
  group: null,
  isInline: false,
};

export default connector(
  connectUsageSetNavFrom,
  connectResponsive,
)(StoryboardPostText);
